/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
/**
 * A React component for creating and editing events.
 * @returns The rendered CreateEditEvent component.
 */
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { geocodeByAddress, getLatLng } from "react-google-places-autocomplete";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { components } from "react-select";

// import "./createEditEvent.css";
// import CreateEditEventComponent from "./createEditEventComponent";

import { getCloudinaryMediaBaseUrl } from "../../../../config/cloudinaryBase";
import {
  htmlToPlainText,
  uploadEventImage,
} from "../../../../helpers/helper.service";
import {
  normalizeTicketOfflineSoldOut,
  pickOfflineAssignedUserIds,
} from "../../../../helpers/eventOfflineAssignment";
import {
  isOnGroundAssignableStaff,
  normalizeOnGroundAssignedUserIds,
  onGroundRoleForRow,
  pickOnGroundAssignedUserIds,
} from "../../../../helpers/eventOnGroundAssignment";
import {
  asClubEmployeesList,
  formatClubEmployeeOptionLabel,
  pickClubEmployeeUserId,
} from "../../../../helpers/clubEmployeeList";
import { asClubOperatorsList } from "../../../../helpers/clubOperatorList";
import { pickClubStaffUserId } from "../../../../helpers/clubStaffInvite";
import { fetchClubEmployees, fetchClubOperators } from "../../../../api/clubApi";
import {
  buildEventCatalogPayload,
  snapshotIdsFromEvent,
  unwrapEventRecord,
  validateEventCatalogAttach,
} from "../../../../helpers/clubCatalog";
import eventService from "../../../../store/features/services/eventServices";
import useAuth from "../../../../hooks/useAuth";
import { artistListResponce } from "../../../../store/features/artists/artistsSlice";
import { categoryListResponce } from "../../../../store/features/category/categorySlice";
import { clubListResponce } from "../../../../store/features/club/clubSlice";
import {
  addEvent,
  currentEventListResponce,
  soldOutEvent,
  updateEvent,
} from "../../../../store/features/event/eventSlice";
import { genresListResponce } from "../../../../store/features/genres/genresSlice";
import { getLanguages } from "../../../../store/features/language/languageSlice";
import { organiserListResponse } from "../../../../store/features/organiser/organiserSlice";
import { showToast } from "../../../../store/features/toast/toastSlice";
import Constant from "../../../../util/constant";
import CreateEditEventComponent1 from "./CreateEditEventComponent1";
import {
  buildTableBookingEventPayload,
  pickEventTableBookingFromItem,
  validateEventTableBooking,
} from "../../../../helpers/eventTableBooking";
import {
  prepareTicketPhasesForApi,
  validateTicketPhases,
} from "../../../../helpers/ticketSalePhases";
import {
  pickTableBookingPriceHike,
  buildTableBookingPriceHikePayload,
} from "../../../../helpers/tableBookingPriceHike";
import {
  pickTableBookingAutoPriceHike,
  buildTableBookingAutoPriceHikePayload,
} from "../../../../helpers/tableBookingAutoPriceHike";
import {
  formatStoredNumber,
  parseNonNegativeRupee,
} from "../../../../helpers/tableFeeSettings";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";

const CREATE_EVENT_DRAFT_KEY = "create_event_draft_v1";

const sanitizeDraftValue = (value) => {
  if (value instanceof File) return undefined;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeDraftValue(item))
      .filter((item) => item !== undefined);
  }
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, val]) => {
      const sanitized = sanitizeDraftValue(val);
      if (sanitized !== undefined) {
        acc[key] = sanitized;
      }
      return acc;
    }, {});
  }
  return value;
};

const parseDraftDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed;
};

function normalizeTicketPassesPerUnit(value) {
  if (value === "" || value == null) return 1;
  const n = parseInt(String(value), 10);
  if (!Number.isFinite(n)) return 1;
  return Math.min(20, Math.max(1, n));
}

function normalizeTicketCoverAmount(value) {
  if (value === "" || value == null) return 0;
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.round(n));
}

function formatGstPercentForForm(value) {
  if (value === "" || value == null) return "0";
  const n = Number(value);
  if (n === 0 || n === 5 || n === 18) return String(n);
  return "0";
}

/**
 * Empty means no GST. On update, omitting the field keeps the saved value,
 * so a cleared input is sent as 0.
 */
function applyGstPercentForApi(ticket) {
  const raw = ticket?.gstPercent;
  const trimmed = raw == null ? "" : String(raw).trim();
  if (trimmed === "") {
    return { ...ticket, gstPercent: 0 };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) {
    const next = { ...ticket };
    delete next.gstPercent;
    return next;
  }
  const clamped = Math.min(100, Math.max(0, n));
  return {
    ...ticket,
    gstPercent: Math.round(clamped * 100) / 100,
  };
}

function formatTicketRowForForm(ticket) {
  const passes =
    ticket?.passesPerUnit != null && ticket?.passesPerUnit !== ""
      ? String(ticket.passesPerUnit)
      : "1";
  const cover =
    ticket?.coverAmount != null && ticket?.coverAmount !== ""
      ? String(ticket.coverAmount)
      : "0";
  return {
    ...ticket,
    offlineSoldOut: normalizeTicketOfflineSoldOut(ticket),
    passesPerUnit: passes,
    coverAmount: cover,
    gstPercent: formatGstPercentForForm(ticket?.gstPercent),
  };
}

function prepareTicketForApi(ticket) {
  const { ticketstatus, ...ticketWithoutSold } = ticket;
  let result = { ...ticketWithoutSold };

  if (result._id === "") {
    const { _id, ...withoutId } = result;
    result = withoutId;
  }
  if (result.deliverycost === "") {
    const { deliverycost, ...withoutDelivery } = result;
    result = withoutDelivery;
  }

  result.passesPerUnit = normalizeTicketPassesPerUnit(result.passesPerUnit);
  result.coverAmount = normalizeTicketCoverAmount(result.coverAmount);
  result = applyGstPercentForApi(result);

  return prepareTicketPhasesForApi(result);
}

const CreateEditEventContainer1 = (props) => {
  /**
   * Initializes various state variables and references used in the component.
   * @returns None
   */
  const navigate = useNavigate();
  const { userId, isOrganiser, isAdmin, isClubManager, managedClubId } =
    useAuth();
  const { state } = useLocation();
  const initialTableBooking = pickEventTableBookingFromItem(state?.item);
  const initialCatalog = snapshotIdsFromEvent(state?.item);
  const isCreatePage = state?.page === "add";
  const [catalogEventItem, setCatalogEventItem] = useState(state?.item || null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const buttonDisable = useRef(false);
  let triggerRef = useRef(null);

  const allLanguages = useSelector((state) => state?.language?.languages);
  const userName = window.localStorage.getItem(
    Constant.SESSIONSTORAGEKEYS.USER_NAME
  );

  const userImage = window.localStorage.getItem(
    Constant.SESSIONSTORAGEKEYS.USER_IMAGE
  );
  const [showStatusModal, setShowStatusModal] = useState(false);

  const organisers = useSelector(
    (state) => state?.organiser.organiserListAPIResponse
  );
  const [organiser, setOrganiser] = useState(
    isOrganiser || isClubManager
      ? [
          {
            label: userName,
            value: userName,
            labelImg: userImage,
            id: userId,
          },
        ]
      : organisers?.map((item) => {
          return {
            label: item?.fullname,
            value: item?._id,
            labelImg: item?.image ? item?.image : "sample.jpg",
            id: item?._id,
          };
        })
  );
  useEffect(() => {
    if (window.localStorage.getItem("role") === Constant.ROLES.ADMIN) {
      setOrganiser([
        {
          label: userName,
          value: userName,
          labelImg: userImage,
          id: userId,
        },
        ...organiser,
      ]);
    }
  }, []);

  useEffect(() => {
    if (!isClubManager) return;
    if (!organisers?.length) return;
    setOrganiser(
      organisers.map((item) => ({
        label: item?.fullname,
        value: item?._id,
        labelImg: item?.image ? item?.image : "sample.jpg",
        id: item?._id,
      }))
    );
  }, [organisers, isClubManager]);

  const reactQuillRefIns = useRef();
  const reactQuillaboutRefIns = useRef();
  const selectInputRef = useRef();
  const artistSelectInputRef = useRef();
  const languageSelectInputRef = useRef();
  const organiserSelectInputRef = useRef();
  const categorySelectInputRef = useRef();
  const genresSelectInputRef = useRef();
  const clubSelectInputRef = useRef();

  const [eventsForm, setEventsForm] = useState(null);
  const [placeHolder, setPlaceHolder] = useState(true);
  const [expirydatetime, setExpirydatetime] = useState();
  const [bookingdatetime, setBookingdatetime] = useState();
  const [latAndLng, setLatAndLng] = useState({ lng: 0, lat: 0 });
  const [textTitlecount, setTextTitlecount] = useState(0);
  const [language, setLanguage] = useState([]);
  const [artistsState, setArtistsState] = useState([]);
  const [genres, setGenres] = useState([]);
  const [organiserState, setOrganiserState] = useState([]);
  const [categoryState, setCategoryState] = useState([]);
  const [dimenstionError, setDimenstionError] = useState(false);
  const [usersigndatetime, setUsersigndatetime] = useState();
  const [bookingEnddatetime, setBookingEnddatetime] = useState();
  const [breadcrumbHeight, setBreadcrumbHeight] = useState("");
  const [isUploadingDocumentLink, setIsUploadingDocumentLink] = useState(false);
  const [aboutTextEditorValueCount, setAboutTextEditorValueCount] = useState();
  const [termsTextEditorValueCount, setTermsTextEditorValueCount] = useState();
  const [profileImage, setProfileImage] = useState();
  const [locationValue, setLocationValue] = useState("");
  const [uploadedDocumentThumnailFile, setuploadedDocumentThumnailFile] =
    useState(null);
  const [daynamicImage, setDaynamicImage] = useState();

  const [islayoutImage, setIsLayoutImage] = useState();
  const [daynamicLayoutImage, setDaynamicLayoutImage] = useState();
  const [uploadedLayoutImageFile, setuploadedLayoutImageFile] = useState(null);
  const [isUploadingLayoutImageLink, setIsUploadingLayoutImageLink] =
    useState(false);

  const [eventStartingDate, setEventStartingDate] = useState(
    state?.page === "add" ? "" : new Date(state?.item?.startdatetime)
  );
  const [bookingStartingDate, setBookingStartingDate] = useState(
    state?.page === "add" ? "" : new Date(state?.item?.bookingstart)
  );
  const [eventEndingDate, setEventEndingDate] = useState(
    state?.page === "add" ? "" : new Date(state?.item?.enddatetime)
  );
  const [bookingEndingDate, setBookingEndingDate] = useState(
    state?.page === "add" ? "" : new Date(state?.item?.bookingend)
  );
  const [isEventStartInvalid, setIsEventStartInvalid] = useState(false);
  const [isBookingStartInvalid, setIsBookingStartInvalid] = useState(false);
  const [isEventEndInvalid, setIsEventEndInvalid] = useState(false);
  const [isBookingEndInvalid, setIsBookingEndInvalid] = useState(false);

  const [ticketRows, setTicketRows] = useState(state?.item?.tickets);
  const normalizedTicketRowsForForm = useMemo(() => {
    if (!ticketRows || !Array.isArray(ticketRows)) return [];
    return ticketRows.map(formatTicketRowForForm);
  }, [ticketRows]);
  const [isChange, setIsChange] = useState(false);
  /**
   * Handles the keydown event and prevents the default behavior if the key pressed is '-' or '+'.
   * @param {{Event}} e - The keydown event object.
   * @returns None
   */
  const handleKeyDown = (e) => {
    if (e.key === "-" || e.key === "+") {
      e.preventDefault();
    }
  };

  /**
   * Constructs the Cloudinary URL by concatenating the environment variables
   * for the Cloudinary URL, name, and subfolder.
   * @returns The constructed Cloudinary URL.
   */
  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();

  const soldout = useSelector(
    (state) => state?.event?.currentEventListResponce
  );
  const foundItem = soldout.find((item) => item?._id === state?.item?._id);
  const soldoutStatus = foundItem ? foundItem?.soldout : false;

  /**
   * Retrieves specific properties from the Redux state using the useSelector hook.
   * @param {{object}} state - The Redux state object.
   * @returns {{
   *    artistResponce: any,
   *    categoryResponce: any,
   *    genresResponce: any,
   *    event: any,
   *    languages: any
   * }} An object containing the desired properties from the Redux state.
   */
  const {
    artist: { artistResponce },
    category: { categoryResponce },
    genres: { genresResponce },
    event,
  } = useSelector((state) => state);

  const clubList = useSelector((state) => state?.club?.clubList ?? []);

  const platformOpt = useMemo(
    () => ({
      label: t("NONE_PLATFORM_WIDE"),
      value: "platform-wide",
      id: null,
    }),
    [t]
  );

  const clubOptionsForEventForm = useMemo(() => {
    if (!isAdmin) return [];
    const useAllClubs = state?.page !== "add";
    const list = useAllClubs
      ? clubList || []
      : (clubList || []).filter((c) => c.isactive !== false);
    const opts = list.map((c) => ({
      label: `${c.name} (${c.slug})${
        c.isactive === false ? ` [${t("CLUB_STATUS_INACTIVE")}]` : ""
      }`,
      value: c._id,
      id: c._id,
    }));
    return [platformOpt, ...opts];
  }, [isAdmin, clubList, state?.page, platformOpt, t]);

  /**
   * Retrieves a list of languages, artists, categories, and genres asynchronously using dispatch.
   * @returns None
   */
  const getList = async () => {
    if (isAdmin || isClubManager) {
      dispatch(clubListResponce());
    }
    dispatch(organiserListResponse());
    dispatch(getLanguages()).then((resultAction) => {
      if (getLanguages.fulfilled.match(resultAction)) {
        setPlaceHolder(false);
      } else if (getLanguages.rejected.match(resultAction)) {
      }
    });
    dispatch(artistListResponce()).then((resultAction) => {
      if (artistListResponce.fulfilled.match(resultAction)) {
      } else if (artistListResponce.rejected.match(resultAction)) {
      }
    });
    dispatch(categoryListResponce()).then((resultAction) => {
      if (categoryListResponce.fulfilled.match(resultAction)) {
      } else if (categoryListResponce.rejected.match(resultAction)) {
      }
    });

    dispatch(genresListResponce()).then((resultAction) => {
      if (genresListResponce.fulfilled.match(resultAction)) {
        setPlaceHolder(false);
      } else if (genresListResponce.rejected.match(resultAction)) {
      }
    });
  };
  useEffect(() => {
    getList();
  }, []);

  useEffect(() => {
    if (!isClubManager || !managedClubId) return;
    if (state?.page === "add" || !state?.item?._id) return;
    const cid = state.item.clubid ?? state.item.clubId;
    if (cid == null || cid === "") return;
    if (String(cid) !== String(managedClubId)) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_MANAGER_EVENT_WRONG_CLUB"),
          type: "error",
        })
      );
      navigate("/events-list", { replace: true });
    }
  }, [
    isClubManager,
    managedClubId,
    state?.page,
    state?.item?._id,
    state?.item?.clubid,
    state?.item?.clubId,
    dispatch,
    navigate,
    t,
  ]);

  /**
   * useEffect hook that updates various state values based on the state.item object.
   * @returns None
   */
  useEffect(() => {
    if (state?.item?.location !== "" && state?.item?.location !== undefined) {
      setLocationValue(state?.item?.location);
    }
    if (state?.item?.aboutevent !== undefined) {
      setAboutTextEditorValueCount(
        htmlToPlainText(state.item.aboutevent).length
      );
    }
    if (state?.item?.terms !== undefined) {
      setTermsTextEditorValueCount(htmlToPlainText(state.item.terms).length);
    }
    if (state?.item?.image !== undefined || state?.item?.image !== "") {
      setDaynamicImage(state?.item?.image);
    }
    if (
      state?.item?.layoutimage !== undefined ||
      state?.item?.layoutimage !== ""
    ) {
      setDaynamicLayoutImage(state?.item?.layoutimage);
    }
  }, []);

  /**
   * A state hook that manages the value of `iscreateEventLoaderButton` and provides a function to update its value.
   * @returns {[boolean, function]} - A tuple containing the current value of `iscreateEventLoaderButton` and a function to update its value.
   */
  const [iscreateEventLoaderButton, setIscreateEventLoaderButton] =
    useState(false);
  const [eventPreview, setEventPreview] = useState(null);
  /**
   * Initializes the state for the aboutTextEditorValue and sets its initial value to the
   * about event value from the state object, or an empty string if it is not available.
   * @param {{string}} state.item.aboutevent - The about event value from the state object.
   * @returns None
   */
  const [aboutTextEditorValue, setAboutTextEditorValue] = useState(
    state?.item?.aboutevent || ""
  );
  /**
   * Initializes a state variable for the value of a terms text editor and a function to update it.
   * @param {{string}} [state?.item?.terms=""] - The initial value for the terms text editor.
   * @returns A state variable and a function to update it.
   */
  const [termsTextEditorValue, setTermsTextEditorValue] = useState(
    state?.item?.terms || ""
  );
  /**
   * Initializes a state variable for the default value of a language.
   * @param {{value: string | undefined, label: string | undefined}} useState - The useState hook.
   * @returns An array containing the default value and a function to update the default value.
   */

  const [languagedefaultValue, setLanguagedefaultValue] = useState(
    state?.page === "add"
      ? []
      : state?.item?.languageList?.map((item) => {
        return {
          label: item?.titleenglish,
          value: item?.titleenglish,
          id: item?._id,
          disabled:false
        };
      })
  );

  /**
   * Initializes the state for genresdefaultValue using the provided data.
   * @param {{Array}} state.item.genre - The array of genre items.
   * @returns None
   */
  const [genresdefaultValue, setGenresdefaultValue] = useState(
    state?.page === "add"
      ? []
      : state?.item?.genre?.map((item) => {
        return {
          label: item?.title,
          value: item?.title,
          id: item?._id,
                disabled: false,
        };
      })
  );


    const [organiserdefaultValue, setOrganiserdefaultValue] = useState(
    state?.page === "add"
      ? {
          label: userName,
          value: userName,
          id: userId,
        }
      : {
          label: state?.item?.organizer[0]?.fullname,
          value: state?.item?.organizer[0]?.fullname,
          id: state?.item?.organizer[0]?._id,
        }
  );

  /**
   * Initializes the state for the category default value using the provided state item.
   * @param {{object}} state - The state object containing the item property.
   * @returns None
   */
  const [categorydefaultValue, setCategorydefaultValue] = useState(
   state?.page === "add"
      ? []:  state?.item?.category?.map((item) => {
      return {
        label: item?.title,
        value: item?.title,
        id: item?._id,
              disabled: false,
      };
    })
  );
  /**
   * Initializes the state for the artists default value using the provided data.
   * @param {{Array}} state.item.artistsDetails - The array of artist details.
   * @returns None
   */

  const [artistsdefaultValue, setArtistsdefaultValue] = useState(
    state?.page === "add"
      ? []: state?.item?.artistsDetails?.map((item) => {
      return {
        label: item?.name,
        value: item?.name,
        labelImg: item?.image,
        id: item?._id,
              disabled: false,
      };
    })
  );

  /**
   * useEffect hook that sets the height of the breadcrumb based on the height of the
   * "page-title-box" element.
   * @returns None
   */
  useEffect(() => {
    setBreadcrumbHeight(
      document.getElementsByClassName("page-title-box")[0].offsetHeight
    );
  }, []);

  /**
   * A dictionary object that maps valid thumbnail file types to their corresponding file extensions.
   * @type {{string: string}}
   */
  const uploadThumNailValid = {
    "image/jpeg": [],
    "image/png": [],
    "image/webp": [],
    "image/jpg": [],
  };
  const uploadLayoutThumNailValid = {
    "image/jpeg": [],
    "image/png": [],
    "image/webp": [],
    "image/jpg": [],
  };

  /**
   * An array of objects representing different artists.
   * Each object has the following properties:
   * - value: The value of the artist (e.g. "English", "Hindi", etc.)
   * - label: The label of the artist (e.g. "English", "Hindi", etc.)
   * - labelImg: The image file name associated with the artist (e.g. "artist10.png", "artist9.png", etc.)
   * @type {Array<Object>}
   */
  let artists = artistResponce?.map((val) => {
    return {
      value: val?.name,
      label: val?.name,
      labelImg: val?.image,
      id: val?._id,
      disabled: false,

    };
  });
  let genresList = genresResponce?.map((val) => {
    return {
      value: val?.title,
      label: val?.title,
      id: val?._id,
      disabled: false,

    };
  });
  let languages = allLanguages?.map((val) => {
    return {
      value: val?.titlelang,
      label: val?.titlelang,
      id: val?._id,
      disabled: false,

    };
  });


  let categoryList = categoryResponce?.map((val) => {
    return {
      value: val?.title,
      label: val?.title,
      id: val?._id,
      disabled: false, // Initialize disable property
    };
  });


    
  const [selectedCategories, setCategories] = useState(categorydefaultValue);
  const [selectedArtist, setSelectedArtist] = useState(artistsdefaultValue);
  const [selectedLanguages, setSelectedLanguages] = useState(languagedefaultValue);
  const [selectedGenre, setSelectedGenre] = useState(genresdefaultValue);

  function updateCategoryList(selectedCategories, categoryList) {
    if (selectedCategories.length >= 5) {
      const selectedIds = new Set(selectedCategories.map(category => category.id));
      return categoryList.map(category => ({
        ...category,
        disabled: !selectedIds.has(category.id),
      }));
    }
    return categoryList;
  }


  categoryList = updateCategoryList(selectedCategories, categoryList);
  languages = updateCategoryList(selectedLanguages, languages);
  genresList = updateCategoryList(selectedGenre, genresList);
  artists = updateCategoryList(selectedArtist, artists);






  /**
   * Updates the events form with the provided form data.
   * @param {{object}} form - The new form data to update the events form with.
   * @returns None
   */
  const createEventForm = (form) => {
    setEventsForm(form);
  };

  /**
   * Handles the change event of a text input field for the title.
   * @param {{Event}} e - The event object.
   * @returns None
   */
  const handleTextTitleChange = (e) => {
    const value = e.target.value;
    setTextTitlecount(value?.length);
  };

  /**
   * Creates a memoized object containing the form fields and their validation rules for an event form.
   * @returns An object containing the form fields and their validation rules.
   */
  const eventForm = {
    venuetobeanounced: {
      name: "venuetobeanounced",
      validate: {
        required: {
          value: false,
        },
      },
    },
    sponsored: {
      name: "sponsored",
      validate: {
        required: {
          value: false,
        },
      },
    },
    is18Plus: {
      name: "is18Plus",
      validate: {
        required: {
          value: false,
        },
      },
    },
    about: {
      name: "about",
      validate: {
        required: {
          value: true,
          message: t("THIS_FIELD_IS_REQUIRED"),
        },
        notEmpty: (value) =>
          htmlToPlainText(value || "").trim().length > 0 ||
          t("THIS_FIELD_IS_REQUIRED"),
      },
    },
    map: {
      name: "map",
      validate: {
        required: {
          value: true,
          message: t("THIS_FIELD_IS_REQUIRED"),
        },
      },
    },
    terms: {
      name: "terms",
      validate: {
        required: {
          value: true,
          message: t("THIS_FIELD_IS_REQUIRED"),
        },
        notEmpty: (value) =>
          htmlToPlainText(value || "").trim().length > 0 ||
          t("THIS_FIELD_IS_REQUIRED"),
      },
    },
    thumbnailfile: {
      name: "thumbnailfile",
      validate: {
        required: {
          value: false,
        },
      },
    },
    organiser: {
      name: "organiser",
      validate: {
        required: {
          value: false,
        },
      },
    },
    club: {
      name: "club",
      validate: {
        required: {
          value: false,
        },
      },
    },
    soldout: {
      name: "soldout",
      validate: {
        required: {
          value: false,
        },
      },
    },
    artist: {
      name: "artist",
      validate: {
        required: {
          value: true,
          message: t("REQUIRED"),
        },
        minSelected: (value) =>
          (Array.isArray(value) && value.length > 0) || t("REQUIRED"),
      },
    },
    category: {
      name: "category",
      validate: {
        required: {
          value: true,
          message: t("REQUIRED"),
        },
        minSelected: (value) =>
          (Array.isArray(value) && value.length > 0) || t("REQUIRED"),
      },
    },
    language: {
      name: "language",
      validate: {
        required: {
          value: true,
          message: t("REQUIRED"),
        },
        minSelected: (value) =>
          (Array.isArray(value) && value.length > 0) || t("REQUIRED"),
      },
    },
    title: {
      name: "title",
      validate: {
        required: {
          value: true,
          message: t("THIS_FIELD_IS_REQUIRED"),
        },
        pattern: {
          value: Constant.REGEX.ALPHANUMERICSPECIALCHAR,
          message: t("ALLOWED_ONLY_ALPHABETS_NUMBERS_AND_SPECIALCHARACTERS"),
        },
        maxLength: {
          value: 70,
          message: t("ALLOWED_MAX_70_CHARACTERS"),
        },
      },
    },
    subtitle: {
      name: "subtitle",
      validate: {
        required: {
          value: false,
        },
        maxLength: {
          value: 200,
        },
      },
    },
    deliverycost: {
      name: "deliverycost",
      validate: {
        required: {
          value: false,
          message: "Delivery amount is required",
        },
        pattern: {
          value: Constant.REGEX.AMOUNT,
          message: t("ENTER_VALID_NUMBER"),
        },
        maxLength: {
          value: 5,
          message: t("MAXIMUM_5_DIGIT_VALUE"),
        },
      },
    },

    ticketprice: {
      name: "ticketprice",
      validate: {
        required: {
          value: true,
          message: "Ticket price is required",
        },
        pattern: {
          value: Constant.REGEX.AMOUNT,
          message: t("ENTER_VALID_NUMBER"),
        },
        maxLength: {
          value: 8,
          message: t("MAXIMUM_8_DIGIT_VALUE"),
        },
      },
    },
    gstPercent: {
      name: "gstPercent",
      validate: {
        validate: (value) => {
          const n = Number(String(value ?? "").trim());
          if (n === 0 || n === 5 || n === 18) return true;
          return t("TICKET_GST_PERCENT_INVALID");
        },
      },
    },
    totaltickets: {
      name: "totaltickets",
      validate: {
        required: {
          value: true,
          message: "Total tickets are required",
        },
        pattern: {
          value: Constant.REGEX.NUMBER1TO9,
          message: t("ENTER_VALID_NUMBER"),
        },
        maxLength: {
          value: 8,
          message: t("MAXIMUM_8_DIGIT_VALUE"),
        },
      },
    },
    tickettype: {
      name: "tickettype",
      validate: {
        required: {
          value: true,
          message: "Ticket type is required",
        },
        pattern: {
          value: Constant.REGEX.ALPHANUMERICSPECIALCHAR,
          message: t("ALLOWED_ONLY_ALPHABETS_NUMBERS_AND_SPECIALCHARACTERS"),
        },
        maxLength: {
          value: 70,
          message: t("ALLOWED_MAX_70_CHARACTERS"),
        },
      },
    },

    description: {
      name: "description",
      validate: {
        required: {
          value: false,
        },
        maxLength: {
          value: 250,
          message: t("ALLOWED_MAX_250_CHARACTERS"),
        },
      },
    },
    genreid: {
      name: "genreid",
      validate: {
        required: {
          value: true,
          message: t("REQUIRED"),
        },
        minSelected: (value) =>
          (Array.isArray(value) && value.length > 0) || t("REQUIRED"),
      },
    },
    eventstartDate: {
      name: "eventstartDate",
      validate: {
        required: {
          value: true,
          message: t("REQUIRED"),
        },
      },
    },
    bookingdate: {
      name: "bookingdate",
      validate: {
        required: {
          value: true,
          message: t("REQUIRED"),
        },
      },
    },
    eventEndDate: {
      name: "eventEndDate",
      validate: {
        required: {
          value: true,
          message: t("REQUIRED"),
        },
      },
    },
    bookingEnd: {
      name: "bookingEnd",
      validate: {
        required: {
          value: true,
          message: t("REQUIRED"),
        },
      },
    },
    layoutimage: {
      name: "layoutimage",
      validate: {
        required: {
          value: false,
        },
      },
    },
    layoutdesc: {
      name: "layoutdesc",
      validate: {
        required: {
          value: false,
        },
        pattern: {
          value: Constant.REGEX.ALPHANUMERICSPECIALCHAR,
          message: t("ALLOWED_ONLY_ALPHABETS_NUMBERS_AND_SPECIALCHARACTERS"),
        },
        maxLength: {
          value: 2000,
          message: t("ALLOWED_MAX_2000_CHARACTERS"),
        },
      },
    },
    categorydesc: {
      name: "categorydesc",
      validate: {
        required: {
          value: false,
        },
        pattern: {
          value: Constant.REGEX.ALPHANUMERICSPECIALCHAR,
          message: t("ALLOWED_ONLY_ALPHABETS_NUMBERS_AND_SPECIALCHARACTERS"),
        },
        maxLength: {
          value: 250,
          message: t("ALLOWED_MAX_250_CHARACTERS"),
        },
      },
    },
    passesPerUnit: {
      name: "passesPerUnit",
      validate: {
        validate: (value) => {
          if (value === "" || value == null) return true;
          const str = String(value).trim();
          if (!Constant.REGEX.NUMONLY.test(str) || !/^\d+$/.test(str)) {
            return t("TICKET_PASSES_PER_UNIT_INVALID");
          }
          const n = parseInt(str, 10);
          if (n < 1 || n > 20) return t("TICKET_PASSES_PER_UNIT_INVALID");
          return true;
        },
      },
    },
    coverAmount: {
      name: "coverAmount",
      validate: {
        validate: (value) => {
          if (value === "" || value == null) return true;
          const str = String(value).trim();
          if (!Constant.REGEX.NUMONLY.test(str) || !/^\d+$/.test(str)) {
            return t("TICKET_COVER_AMOUNT_INVALID");
          }
          if (Number(str) < 0) return t("TICKET_COVER_AMOUNT_INVALID");
          return true;
        },
      },
    },
    bookingpercentage: {
      name: "bookingpercentage",
      validate: {
        required: {
          value: true,
          message: t("THIS_FIELD_IS_REQUIRED"),
        },
        pattern: {
          value: Constant.REGEX.PARENTAMOUNT,
          message: t("ENTER_VALID_NUMBER"),
        },
        maxLength: {
          value: 5,
          message: t("MAXIMUM_5_DIGIT_VALUE"),
        },
      },
    },
    tableBookingFixedFee: {
      name: "tableBookingFixedFee",
      validate: {
        validate: (value) => {
          if (!isAdmin && !isClubManager) return true;
          if (parseNonNegativeRupee(value) == null) {
            return t("TABLE_BOOKING_FIXED_FEE_INVALID");
          }
          return true;
        },
      },
    },
  };

  /**
   * Updates the location value when the autocomplete input value changes.
   * @param {{any}} value - The new value of the autocomplete input.
   * @returns None
   */
  const onChangeAutocomplate = async (value) => {
    try {
      const results = await geocodeByAddress(value?.label);
      if (results && results?.length > 0) {
        const latLng = await getLatLng(results[0]);
        setIsChange(true);
        setLatAndLng(latLng);
      } else {
        console.error("No results found for the selected place");
      }
    } catch (error) {
      console.error("Error fetching geolocation:", error);
    }
    setLocationValue(value?.label);
  };

  /**
   * A custom input option component for a select dropdown.
   * @param {{getStyles}} getStyles - Function to get the styles for the component.
   * @param {{Icon}} Icon - The icon component to display.
   * @param {{boolean}} isDisabled - Whether the option is disabled.
   * @param {{boolean}} isSelected - Whether the option is selected.
   * @param {{ReactNode}} children - The content of the option.
   * @param {{object}} innerProps - Additional props for the inner elements.
   * @param {{...rest}} rest - Additional props for the component.
   * @returns The rendered input option component.
   */
  const InputOption = ({
    getStyles,
    Icon,
    isDisabled,
    isSelected,
    children,
    innerProps,
    ...rest
  }) => {
    const props = {
      ...innerProps,
    };

    return (
      <components.Option
        {...rest}
        isDisabled={isDisabled}
        isSelected={isSelected}
        getStyles={getStyles}
        innerProps={props}
      >
        <input type="checkbox" checked={isSelected} />
        <label for="checkbox">
          <span></span>
        </label>
        {children}
      </components.Option>
    );
  };

  /**
   * Handles the action when a check event occurs.
   * @param {{boolean}} e - The event that triggered the action.
   * @returns None
   */
  const onCheckAction = (e) => {
    if (!buttonDisable.current) {
      buttonDisable.current = true;
      if (e === true) {
        const params = {
          id: state?.item?._id,
          status: true,
        };
        dispatch(soldOutEvent(params)).then((resultAction) => {
          if (soldOutEvent.fulfilled.match(resultAction)) {
            dispatch(
              showToast({
                show: false,
                message: resultAction?.payload?.res?.message,
                type: "success",
              })
            );
            setTimeout(() => {
              buttonDisable.current = false;
            }, 500);
          } else if (soldOutEvent.rejected.match(resultAction)) {
            dispatch(
              showToast({
                show: false,
                message: resultAction?.error?.message,
                type: "error",
              })
            );
            setTimeout(() => {
              buttonDisable.current = false;
            }, 500);
          }
        });
      } else {
        const params = {
          id: state?.item?._id,
          status: false,
        };
        dispatch(soldOutEvent(params)).then((resultAction) => {
          if (soldOutEvent.fulfilled.match(resultAction)) {
            dispatch(
              showToast({
                show: false,
                message: resultAction?.payload?.res?.message,
                type: "success",
              })
            );
            setTimeout(() => {
              buttonDisable.current = false;
            }, 500);
          } else if (soldOutEvent.rejected.match(resultAction)) {
            dispatch(
              showToast({
                show: false,
                message: resultAction?.error?.message,
                type: "error",
              })
            );
            setTimeout(() => {
              buttonDisable.current = false;
            }, 500);
          }
        });
      }
    }
  };

  /**
   * Clears the uploaded thumbnail file, profile image, and resets the dimension error flag.
   * @returns None
   */
  const handleClearUploadedThumnailFile = () => {
    setDaynamicImage(undefined);
    setuploadedDocumentThumnailFile(null);
    setProfileImage(null);
    setDimenstionError(false);
  };

  const onBackArrow = () => {
    navigate("/events-list");
  };

  /**
   * Handles the upload of a thumbnail file.
   * @param {{FileList}} files - The list of files to upload.
   * @returns None
   */
  const handleUploadThumnailFile = (files) => {
    if (files && files?.length > 0) {
      // Files were selected
      setuploadedDocumentThumnailFile(files);
      const reader = new FileReader();
      reader.addEventListener("load", (event) => {
        setProfileImage(event?.target?.result);
      });
      reader.readAsDataURL(files[0]);
    } else {
      // No files were selected (set to null or undefined)
      setuploadedDocumentThumnailFile(null); // or setuploadedDocumentThumnailFile(undefined);
      setProfileImage(null); // or setProfileImage(undefined);
    }
  };

  /**
   * Handles the upload of a thumbnail image file.
   * @param {{FileList}} files - The list of files to upload.
   * @param {{function}} loaderState - The function to update the loader state.
   * @param {{function}} uploadFunction - The function to handle the upload of the files.
   * @returns None
   */
  const handleUploadThumnail = (files, loaderState, uploadFunction) => {
    loaderState(true);
    setTimeout(() => {
      uploadFunction(files);
      loaderState(false);
    }, 1000);
  };

  const handleClearUploadedLayoutImageFile = () => {
    setDaynamicLayoutImage(undefined);
    setuploadedLayoutImageFile(null);
    setIsLayoutImage(null);
    setDimenstionError(false);
  };

  const handleUploadLayoutThumnailFile = (files) => {
    if (files && files?.length > 0) {
      // Files were selected
      setuploadedLayoutImageFile(files);
      const reader = new FileReader();
      reader.addEventListener("load", (event) => {
        setIsLayoutImage(event?.target?.result);
      });
      reader.readAsDataURL(files[0]);
    } else {
      // No files were selected (set to null or undefined)
      setuploadedLayoutImageFile(null); // or setuploadedLayoutImageFile(undefined);
      setIsLayoutImage(null); // or setIsLayoutImage(undefined);
    }
  };

  /**
   * Handles the upload of a thumbnail image file.
   * @param {{FileList}} files - The list of files to upload.
   * @param {{function}} loaderState - The function to update the loader state.
   * @param {{function}} uploadFunction - The function to handle the upload of the files.
   * @returns None
   */
  const handleUploadLayoutThumnail = (files, loaderState, uploadFunction) => {
    loaderState(true);
    setTimeout(() => {
      uploadFunction(files);
      loaderState(false);
    }, 1000);
  };

  /**
   * Handles the form submission by updating the state and displaying a success toast message.
   * @param {{object}} data - The form data submitted.
   * @returns None
   */
  const resolveEventImageField = async (field, existingUrl) => {
    if (field == null || field === "") return undefined;
    if (Array.isArray(field) && field.length > 0) {
      const first = field[0];
      if (first instanceof File) {
        return uploadEventImage(first);
      }
    }
    if (typeof field === "string" && field.trim() !== "") {
      return field;
    }
    return existingUrl;
  };

  // Employees stay on offline POS. Operators are on-ground only. Both ids are user _id.
  const splitStaffAssignment = (data) => {
    const selected = normalizeOnGroundAssignedUserIds(
      data?.offlineAssignedUserIds
    );
    const roleById = new Map(
      (clubEmployeeOptions || []).map((option) => [
        String(option.id),
        option.role,
      ])
    );
    const previousOffline = new Set(
      pickOfflineAssignedUserIds(state?.item).map(String)
    );
    const previousOnGround = new Set(
      pickOnGroundAssignedUserIds(state?.item).map(String)
    );
    const offlineAssignedUserIds = [];
    const onGroundAssignedUserIds = [];
    selected.forEach((id) => {
      const role = roleById.get(String(id));
      const operatorOnly =
        role === "CLUB_OPERATOR" ||
        (!role &&
          previousOnGround.has(String(id)) &&
          !previousOffline.has(String(id)));
      if (!operatorOnly) offlineAssignedUserIds.push(id);
      onGroundAssignedUserIds.push(id);
    });
    return { offlineAssignedUserIds, onGroundAssignedUserIds };
  };

  const rejectFormExtras = (data) => {
    const tableBookingError = validateEventTableBooking(data, t);
    if (tableBookingError) {
      dispatch(
        showToast({
          show: false,
          message: tableBookingError,
          type: "error",
        })
      );
      return true;
    }
    const phaseError = (data?.tickets || [])
      .map((tk) => validateTicketPhases(tk, t))
      .find(Boolean);
    if (phaseError) {
      dispatch(
        showToast({
          show: false,
          message: phaseError,
          type: "error",
        })
      );
      return true;
    }
    const catalogError = validateEventCatalogAttach(
      data,
      catalogEventItem || state?.item,
      t
    );
    if (catalogError) {
      dispatch(
        showToast({
          show: false,
          message: catalogError,
          type: "error",
        })
      );
      return true;
    }
    return false;
  };

  const handleOpenEventPreview = (data) => {
    if (rejectFormExtras(data)) return;
    setEventPreview({
      data,
      labels: {
        languages: selectedLanguages || [],
        genres: selectedGenre || [],
        artists: selectedArtist || [],
        categories: selectedCategories || [],
        organiser: organiserdefaultValue || null,
      },
    });
  };

  const handleCloseEventPreview = () => {
    if (iscreateEventLoaderButton) return;
    setEventPreview(null);
  };

  const handleFormSubmit = async (data) => {
    if (rejectFormExtras(data)) return;

    setIscreateEventLoaderButton(true);
    const aboutPlain = htmlToPlainText(data?.about);
    const termsPlain = htmlToPlainText(data?.terms);
    const eventStartDate = new Date(data?.eventstartDate);
    const eventEndDate = new Date(data?.eventEndDate);
    const bookingStartDate = new Date(data?.bookingdate);
    const bookingEndDate = new Date(data?.bookingEnd);
    let url;
    let layoutimage;
    const hasThumb =
      data?.thumbnailfile != null &&
      data?.thumbnailfile !== "" &&
      (!Array.isArray(data.thumbnailfile) ||
        data.thumbnailfile.length > 0);
    const hasLayout =
      data?.layoutimage != null &&
      data?.layoutimage !== "" &&
      (!Array.isArray(data.layoutimage) || data.layoutimage.length > 0);
    try {
      if (hasThumb) {
        url = await resolveEventImageField(data.thumbnailfile, daynamicImage);
      }
      if (hasLayout) {
        layoutimage = await resolveEventImageField(
          data.layoutimage,
          daynamicLayoutImage
        );
      }
    } catch (e) {
      setIscreateEventLoaderButton(false);
      dispatch(
        showToast({
          show: false,
          message: e?.message || "Image upload failed",
          type: "error",
        })
      );
      return;
    }

    let addParams = {
      title: data?.title,
      image: url === undefined ? "" : url,
      layoutimage: layoutimage ? layoutimage : "",
      startdatetime: eventStartDate.toISOString(),
      enddatetime: eventEndDate.toISOString(),
      bookingstart: bookingStartDate.toISOString(),
      bookingend: bookingEndDate.toISOString(),
      language:  data?.language[0]?.id === undefined ? data?.language : data?.language?.map((val)=>val.id),
      genreid: data?.genreid[0]?.id === undefined ? data?.genreid : data?.genreid?.map((val)=>val.id),
      categoryid:  data?.category[0]?.id === undefined ? data?.category : data?.category?.map((val)=>val.id),
      organizerid:data?.organiser?.length > 0 || data?.organiser?.id
          ? data?.organiser?.id
          : userId,
      artists:data?.artist[0]?.id === undefined ? data?.artist : data?.artist.map((val)=>val.id),
      venuetobeanounced: data?.venuetobeanounced,
      sponsoredEvents: data?.sponsoredEvents,
      aboutevent: aboutPlain,
      terms: termsPlain,
      sponsored: data?.sponsored,
      is18Plus: Boolean(data?.is18Plus),
      ...(isAdmin || isClubManager ? splitStaffAssignment(data) : {}),

      tickets: data?.tickets.map(prepareTicketForApi),
      ...buildTableBookingPriceHikePayload(
        data?.tableBookingPriceHike || pickTableBookingPriceHike(null)
      ),
      ...buildTableBookingAutoPriceHikePayload(
        data?.tableBookingAutoPriceHike || pickTableBookingAutoPriceHike(null)
      ),
      layoutdesc: data?.layoutdesc,
      bookingpercentage: data?.bookingpercentage === ("" || null) ? 0 : data?.bookingpercentage,
      ...(isAdmin || isClubManager
        ? {
            tableBookingFixedFee: parseNonNegativeRupee(data?.tableBookingFixedFee),
          }
        : {}),
      ...buildTableBookingEventPayload(data),
    };
    if (isAdmin) {
      const cid = data?.club?.id;
      if (cid) addParams.clubid = cid;
    } else if (isClubManager && managedClubId) {
      addParams.clubid = managedClubId;
    }
    const addClubId = addParams.clubid;
    if (addClubId) {
      Object.assign(
        addParams,
        buildEventCatalogPayload({
          clubId: addClubId,
          addonItemIds: data?.addonItemIds,
          preorderItemIds: data?.preorderItemIds,
          addonPriceOverrides: data?.addonPriceOverrides,
          preorderPriceOverrides: data?.preorderPriceOverrides,
          addonStockQty: data?.addonStockQty,
          preorderStockQty: data?.preorderStockQty,
        })
      );
    }
    if (locationValue && latAndLng) {
      addParams = {
        location: locationValue,
        latlong: [latAndLng.lng, latAndLng.lat],
        ...addParams,
      };
    }

    let updateParams = {
      title: data?.title,
      image: url === undefined ? "" : url,
      layoutimage: layoutimage ? layoutimage : "",
      startdatetime: eventStartDate.toISOString(),
      enddatetime: eventEndDate.toISOString(),
      bookingstart: bookingStartDate.toISOString(),
      bookingend: bookingEndDate.toISOString(),
      language:data?.language[0]?.id === undefined ? data?.language : data?.language?.map((val)=>val.id),
      genreid:  data?.genreid[0]?.id === undefined ? data?.genreid : data?.genreid.map((val)=>val.id) ,
      categoryid:  data?.category[0]?.id === undefined ? data?.category : data?.category.map((val)=>val.id),
      organizerid: data?.organiser?.id ? data?.organiser?.id : userId,
      artists: data?.artist[0]?.id === undefined ? data?.artist : data?.artist.map((val)=>val.id) ,
      venuetobeanounced: data?.venuetobeanounced,
      aboutevent: aboutPlain,
      terms: termsPlain,
      tickets: data?.tickets.map(prepareTicketForApi),
      ...buildTableBookingPriceHikePayload(
        data?.tableBookingPriceHike || pickTableBookingPriceHike(state?.item)
      ),
      ...buildTableBookingAutoPriceHikePayload(
        data?.tableBookingAutoPriceHike ||
          pickTableBookingAutoPriceHike(state?.item)
      ),
      id: state?.item?._id,
      sponsored: data?.sponsored,
      is18Plus: Boolean(data?.is18Plus),
      ...(isAdmin || isClubManager ? splitStaffAssignment(data) : {}),
      layoutdesc: data?.layoutdesc,
      bookingpercentage: data?.bookingpercentage === ("" || null) ? 0 : data?.bookingpercentage,
      ...(isAdmin || isClubManager
        ? {
            tableBookingFixedFee: parseNonNegativeRupee(data?.tableBookingFixedFee),
          }
        : {}),
      ...buildTableBookingEventPayload(data),
    };

    if (isAdmin) {
      updateParams.clubid = data?.club?.id ?? null;
    } else if (isClubManager && managedClubId) {
      updateParams.clubid = managedClubId;
    }
    const updateClubId = updateParams.clubid;
    if (updateClubId) {
      Object.assign(
        updateParams,
        buildEventCatalogPayload({
          clubId: updateClubId,
          addonItemIds: data?.addonItemIds,
          preorderItemIds: data?.preorderItemIds,
          addonPriceOverrides: data?.addonPriceOverrides,
          preorderPriceOverrides: data?.preorderPriceOverrides,
          addonStockQty: data?.addonStockQty,
          preorderStockQty: data?.preorderStockQty,
        })
      );
    }
    if (locationValue && latAndLng) {
      const existingCoordinates = state?.item?.latlong?.coordinates;
      updateParams = {
        ...updateParams,
        location: locationValue || state?.item?.location,
        latlong: isChange
          ? [latAndLng.lng, latAndLng.lat]
          : Array.isArray(existingCoordinates)
          ? existingCoordinates
          : existingCoordinates &&
            typeof existingCoordinates === "object"
          ? Object.values(existingCoordinates)
          : [latAndLng.lng, latAndLng.lat],
      };
    }

    const refetchClubEventsLists = () => {
      const cid =
        (isClubManager && managedClubId) ||
        data?.club?.id ||
        state?.item?.clubid ||
        state?.item?.clubId;
      if (!cid) return;
      const clubid = String(cid);
      dispatch(currentEventListResponce({ clubid }));
    };

    if (state?.page === "add") {
      dispatch(addEvent(addParams)).then(async (resultAction) => {
        if (addEvent.fulfilled.match(resultAction)) {
          clearCreateEventDraft();
          refetchClubEventsLists();
          dispatch(
            showToast({
              show: false,
              message: resultAction?.payload?.message,
              type: "success",
            })
          );
          navigate("/events-list");
          setIscreateEventLoaderButton(false);
        } else if (addEvent.rejected.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message: resultAction?.error?.message,
              type: "error",
            })
          );
          setIscreateEventLoaderButton(false);
        }
      });
    } else {
      setIscreateEventLoaderButton(true);

      dispatch(updateEvent(updateParams)).then(async (resultAction) => {
        if (updateEvent.fulfilled.match(resultAction)) {
          refetchClubEventsLists();
          dispatch(
            showToast({
              show: false,
              message: resultAction?.payload?.message,
              type: "success",
            })
          );
          navigate("/events-list");
          setIscreateEventLoaderButton(false);
        } else if (updateEvent.rejected.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message: resultAction?.error?.message,
              type: "error",
            })
          );
          setIscreateEventLoaderButton(false);
        }
      });
    }
  };

  /**
   * useEffect hook that formats the usersigndatetime and bookingEnddatetime values
   * when they change.
   * @param {{function}} formatDate - The function used to format the date.
   * @param {{Date}} usersigndatetime - The initial value of the usersigndatetime.
   * @param {{Date}} bookingEnddatetime - The initial value of the bookingEnddatetime.
   * @returns None
   */
  useEffect(() => {
    const initialDate = usersigndatetime;
    const initialFormattedDate = formatDate(initialDate);
    setUsersigndatetime(initialFormattedDate);

    const bookingEndinitialDate = bookingEnddatetime;
    const bookingEndinitialFormattedDate = formatDate(bookingEndinitialDate);
    setBookingEnddatetime(bookingEndinitialFormattedDate);
  }, [usersigndatetime, bookingEnddatetime]);

  /**
   * useEffect hook that updates the usersigndatetime state variable whenever the
   * state.item.enddatetime changes.
   * @param {{Date}} state.item.enddatetime - The end date and time of the item.
   * @returns None
   */
  useEffect(() => {
    const initialDate = state?.item?.enddatetime;
    const initialFormattedDate = formatDate(initialDate);
    setUsersigndatetime(initialFormattedDate);
  }, [state?.item?.enddatetime]);

  useEffect(() => {
    const initialDate = state?.item?.bookingend;
    const initialFormattedDate = formatDate(initialDate);
    setBookingEnddatetime(initialFormattedDate);
  }, [state?.item?.bookingend]);
  /**
   * useEffect hook that formats the expirydatetime and bookingdatetime values
   * when they change.
   * @param {{function}} formatDate - A function that formats a date value.
   * @param {{Date}} expirydatetime - The expiry date and time.
   * @param {{Date}} bookingdatetime - The booking date and time.
   * @returns None
   */
  useEffect(() => {
    const initialDate = expirydatetime;
    const initialFormattedDate = formatDate(initialDate);
    setExpirydatetime(initialFormattedDate);

    const bookinginitialDate = bookingdatetime;
    const bookinginitialFormattedDate = formatDate(bookinginitialDate);
    setBookingdatetime(bookinginitialFormattedDate);
  }, [expirydatetime, bookingdatetime]);

  useEffect(() => {
    const initialDate = state?.item?.startdatetime;
    const initialFormattedDate = formatDate(initialDate);
    setExpirydatetime(initialFormattedDate);
  }, [state?.item?.startdatetime]);

  useEffect(() => {
    const initialDate = state?.item?.bookingstart;
    const initialFormattedDate = formatDate(initialDate);
    setBookingdatetime(initialFormattedDate);
  }, [state?.item?.bookingstart]);
  /**
   * Formats a given date into a string with the format "day month year, hours:minutes AM/PM".
   * @param {{Date}} inputDate - The date to format.
   * @returns A formatted string representing the input date.
   */
  function formatDate(inputDate) {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const dateObject = new Date(inputDate);
    const day = dateObject.getDate();
    const month = months[dateObject.getMonth()];
    const year = dateObject.getFullYear();
    const hours = dateObject.getHours();
    const minutes = dateObject.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, "0");

    const formattedDate = `${day} ${month} ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
    return formattedDate;
  }

  /**
   * Formats the current date, expiry date, and booking date into the specified format.
   * @param {{Date}} currentDate - The current date.
   * @param {{string}} currentFormat - The format to use for the current date.
   * @param {{Date}} expireDate - The expiry date.
   * @param {{string}} expireFormat - The format to use for the expiry date.
   * @param {{Date}} bookingDate - The booking date.
   * @param {{string}} bookingFormat - The format to use for the booking date.
   * @returns {{
   *  currentformattedDate: string,
   *  expireformattedDate: string,
   *  bookingformattedDate: string
   * }} An object containing the formatted current date, expiry date
   */

  const expireDate =
    state?.page === "add" ? expirydatetime : state?.item?.startdatetime;
  const expireFormat = "YYYY-MM-DD HH:mm";
  const expireformattedDate = moment(expireDate).format(expireFormat);

  const expireDateEnd = state?.item?.enddatetime;
  const expireFormatEnd = "YYYY-MM-DD HH:mm";
  const expireformattedDateEnd = moment(expireDateEnd).format(expireFormatEnd);

  const bookingDateEnd = state?.item?.bookingend;

  const bookingexpireFormatEnd = "YYYY-MM-DD HH:mm";
  const bookingexpireformattedDateEnd = moment(bookingDateEnd).format(
    bookingexpireFormatEnd
  );

  const bookingDate =
    state?.page === "add" ? bookingdatetime : state?.item?.bookingstart;

  const bookingFormat = "YYYY-MM-DD HH:mm";
  const bookingformattedDate = moment(bookingDate).format(bookingFormat);

  /**
   * Formats the label and image for an option in a select dropdown.
   * @param {{value: any, label: string, labelImg: string}} option - The option object containing the value, label, and label image.
   * @returns The formatted label and image as a React component.
   */
  const formatOptionLabel = ({ value, label, labelImg }) => (
    <div className="imageLable" style={{ display: "flex" }}>
      <img
        src={
          !labelImg
            ? CLOUDINARY_URL + "dummy-profile.png"
            : CLOUDINARY_URL + labelImg
        }
        width={32}
        alt="artist profile"
      />
      <div>{label}</div>
    </div>
  );

  /**
   * Represents a page title.
   * @type {{Array<{title: string}>}}
   */
  const pageTitle = [
    {
      title: "Events",
      path: "/events-list",
    },
    {
      title: "Events List",
      path: "/events-list",
    },
  ];

  /**
   * An array of formatting options for text.
   * @type {string[]}
   */
  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "color",
  ];

  useEffect(() => {
    const disableScroll = (event) => {
      if (document.activeElement.type === "number") {
        event.preventDefault();
      }
    };

    // Add an event listener to disable scrolling
    document.addEventListener("wheel", disableScroll, { passive: false });

    return () => {
      // Remove the event listener when the component is unmounted
      document.removeEventListener("wheel", disableScroll);
    };
  }, []);

  const renderCustomDateLabel = (value, onClick, placeholderKey, fieldName) => {
    const hasValidDate = moment(value).isValid();
    const dateTextStyle = hasValidDate
      ? {
          color: "var(--white)",
          WebkitTextFillColor: "var(--white)",
        }
      : {
          color: "#9aa4b2",
          WebkitTextFillColor: "#9aa4b2",
        };
    return (
      <div className="inputDatepickerRow" data-rhf-field={fieldName}>
        <div
          role="button"
          tabIndex={0}
          className="customDateInput customDateTrigger"
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onTouchStart={(event) => {
            event.preventDefault();
          }}
          onClick={onClick}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick?.(event);
            }
          }}
          style={{ WebkitTouchCallout: "none" }}
        >
          <span
            className={hasValidDate ? "customDateValue" : "customDatePlaceholder"}
            style={dateTextStyle}
          >
            {hasValidDate
              ? moment(value).format("D MMM YY, h:mm A")
              : t(placeholderKey)}
          </span>
        </div>
      </div>
    );
  };

  const CustomLabelEventStart = ({ value, onClick }) => {
    return renderCustomDateLabel(
      value,
      onClick,
      "SELECT_EVENT_START_TIMINGS",
      "eventstartDate"
    );
  };
  const CustomLabelEventEnd = ({ value, onClick }) => {
    return renderCustomDateLabel(
      value,
      onClick,
      "SELECT_EVENT_END_TIMINGS",
      "eventEndDate"
    );
  };
  const CustomLabelBookingStart = ({ value, onClick }) => {
    return renderCustomDateLabel(
      value,
      onClick,
      "SELECT_BOOKING_START_TIMINGS",
      "bookingdate"
    );
  };
  const CustomLabelBookingEnd = ({ value, onClick }) => {
    return renderCustomDateLabel(
      value,
      onClick,
      "SELECT_BOOKING_END_TIMINGS",
      "bookingEnd"
    );
  };

  const onChangeEventStartingDate = (date) => {
    setEventStartingDate(date);
    // || moment(date).format("YYYY-MM-DD HH:mm") <  moment(bookingStartingDate).format("YYYY-MM-DD HH:mm")
    if (
      moment(date).format("YYYY-MM-DD HH:mm") >=
      moment(eventEndingDate).format("YYYY-MM-DD HH:mm")
    ) {
      setEventEndingDate();
    } else if (
      moment(bookingStartingDate).isValid() &&
      moment(date).format("YYYY-MM-DD HH:mm") <
        moment(bookingStartingDate).format("YYYY-MM-DD HH:mm")
    ) {
      setBookingStartingDate();
      setBookingEndingDate();
    } else if (
      moment(eventEndingDate).isValid() &&
      moment(bookingEndingDate).isValid() &&
      moment(bookingEndingDate).format("YYYY-MM-DD HH:mm") >
        moment(eventEndingDate).format("YYYY-MM-DD HH:mm")
    ) {
      setBookingEndingDate();
    }
    if (
      moment(date).format("YYYY-MM-DD HH:mm") <
      moment(new Date()).format("YYYY-MM-DD HH:mm")
    ) {
      setIsEventStartInvalid(true);
    } else {
      setIsEventStartInvalid(false);
    }
  };

  const onChangeBookingStartingDate = (date) => {
    setBookingStartingDate(date);
    if (
      moment(date).format("YYYY-MM-DD HH:mm") <
      moment(new Date()).format("YYYY-MM-DD HH:mm")
    ) {
      setBookingStartingDate();
      // setBookingEndingDate();
    }
    if (
      moment(date).format("YYYY-MM-DD HH:mm") <
      moment(new Date()).format("YYYY-MM-DD HH:mm")
    ) {
      setIsBookingStartInvalid(true);
    } else {
      setIsBookingStartInvalid(false);
    }
  };

  const onChangeEventEndingDate = (date) => {
    setEventEndingDate(date);

    if (
      moment(bookingEndingDate).isValid() &&
      moment(bookingEndingDate).format("YYYY-MM-DD HH:mm") >
        moment(date).format("YYYY-MM-DD HH:mm")
    ) {
      setBookingEndingDate();
    }

    if (
      moment(eventStartingDate).format("YYYY-MM-DD HH:mm") >
      moment(date).format("YYYY-MM-DD HH:mm")
    ) {
      setIsEventEndInvalid(true);
    } else {
      setIsEventEndInvalid(false);
    }
  };
  const onChangeBookingEndingDate = (date) => {
    setBookingEndingDate(date);
    if (
      moment(bookingStartingDate).format("YYYY-MM-DD HH:mm") >
      moment(date).format("YYYY-MM-DD HH:mm")
    ) {
      setIsBookingEndInvalid(true);
    } else if (
      moment(eventEndingDate).isValid() &&
      moment(date).format("YYYY-MM-DD HH:mm") >
        moment(eventEndingDate).format("YYYY-MM-DD HH:mm")
    ) {
      setIsBookingEndInvalid(true);
    } else {
      setIsBookingEndInvalid(false);
    }
  };

  useEffect(() => { }, [eventEndingDate, eventStartingDate]);
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState,
    trigger,
    setFocus,
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: {
      title: state?.page === "add" ? "" : state?.item?.title,
      layoutdesc: state?.page === "add" ? "" : state?.item?.layoutdesc,
      bookingpercentage:
        state?.page === "add" ? "" : state?.item?.bookingpercentage,
      tableBookingFixedFee:
        state?.page === "add"
          ? ""
          : formatStoredNumber(
              state?.item?.tableBookingFixedFee ??
                state?.item?.table_booking_fixed_fee
            ),
      thumbnailfile: state?.page === "add" ? [] : state?.item?.image,
      layoutimage: state?.page === "add" ? [] : state?.item?.layoutimage,
      eventstartDate: state?.page === "add" ? "" : expireformattedDate,
      eventEndDate: state?.page === "add" ? "" : expireformattedDateEnd,
      terms: state?.page === "add" ? "" : termsTextEditorValue,
      about: state?.page === "add" ? "" : aboutTextEditorValue,
      offlineAssignedUserIds: [
        ...new Set([
          ...pickOfflineAssignedUserIds(state?.item),
          ...pickOnGroundAssignedUserIds(state?.item),
        ]),
      ],
      tickets:
        state?.page === "add"
          ? [
              {
                tickettype: "",
                ticketprice: "",
                gstPercent: "0",
                totaltickets: "",
                deliverycost: "",
                passesPerUnit: "1",
                coverAmount: "0",
                soldout: false,
                offlineSoldOut: false,
                phasedPricingEnabled: false,
                phases: [],
              },
            ]
          : normalizedTicketRowsForForm,
      bookingdate: state?.page === "add" ? "" : bookingformattedDate,
      bookingEnd: state?.page === "add" ? "" : bookingexpireformattedDateEnd,
      language: state?.page === "add" ? [] : languagedefaultValue,
      genreid: state?.page === "add" ? [] : genresdefaultValue,
      organiser: state?.page === "add" ? [] : organiserdefaultValue?.value,
      artist: state?.page === "add" ? [] : artistsdefaultValue,
      category: state?.page === "add" ? [] : categorydefaultValue,
      map: state?.page === "add" ? "" : state?.item?.location,
      venuetobeanounced:
        state?.page === "add" ? false : state?.item?.venuetobeanounced,
      sponsored: state?.page === "add" ? false : state?.item?.sponsored,
      is18Plus: state?.page === "add" ? false : Boolean(state?.item?.is18Plus),
      club: null,
      tableBookingEnabled: initialTableBooking.tableBookingEnabled,
      tableBookingLayoutId: initialTableBooking.tableBookingLayoutId,
      tableBookingViewkey: initialTableBooking.tableBookingViewkey,
      tableBookingSlotKey: initialTableBooking.tableBookingSlotKey,
      tableBookingAreaPricing: initialTableBooking.tableBookingAreaPricing,
      tableBookingPriceHike: pickTableBookingPriceHike(state?.item),
      tableBookingAutoPriceHike: pickTableBookingAutoPriceHike(state?.item),
      addonItemIds: initialCatalog.addonItemIds,
      preorderItemIds: initialCatalog.preorderItemIds,
      addonPriceOverrides: initialCatalog.addonPriceOverrides,
      preorderPriceOverrides: initialCatalog.preorderPriceOverrides,
      addonStockQty: initialCatalog.addonStockQty,
      preorderStockQty: initialCatalog.preorderStockQty,
    },
  });
  const watchedLanguageIds = useWatch({ control, name: "language" });
  const watchedGenreIds = useWatch({ control, name: "genreid" });
  const watchedArtistIds = useWatch({ control, name: "artist" });
  const watchedCategoryIds = useWatch({ control, name: "category" });

  const mapIdsToOptions = useCallback((ids, options) => {
    if (!Array.isArray(ids) || !Array.isArray(options)) return [];
    const idSet = new Set(
      ids
        .map((item) => {
          if (item == null) return null;
          if (typeof item === "object") {
            return String(item.id ?? item._id ?? item.value ?? "");
          }
          return String(item);
        })
        .filter(Boolean)
    );
    if (!idSet.size) return [];
    return options.filter((opt) => idSet.has(String(opt?.id)));
  }, []);

  const sameOptionIds = useCallback((left, right) => {
    const leftIds = (Array.isArray(left) ? left : [])
      .map((x) => String(x?.id))
      .sort();
    const rightIds = (Array.isArray(right) ? right : [])
      .map((x) => String(x?.id))
      .sort();
    if (leftIds.length !== rightIds.length) return false;
    return leftIds.every((id, index) => id === rightIds[index]);
  }, []);

  useEffect(() => {
    if (!isCreatePage) return;
    const hydrated = mapIdsToOptions(watchedLanguageIds, languages);
    if (!sameOptionIds(selectedLanguages, hydrated)) {
      setSelectedLanguages(hydrated);
      setLanguagedefaultValue(hydrated);
    }
  }, [
    languages,
    mapIdsToOptions,
    sameOptionIds,
    selectedLanguages,
    watchedLanguageIds,
    isCreatePage,
  ]);

  useEffect(() => {
    if (!isCreatePage) return;
    const hydrated = mapIdsToOptions(watchedGenreIds, genresList);
    if (!sameOptionIds(selectedGenre, hydrated)) {
      setSelectedGenre(hydrated);
      setGenresdefaultValue(hydrated);
    }
  }, [
    genresList,
    mapIdsToOptions,
    sameOptionIds,
    selectedGenre,
    watchedGenreIds,
    isCreatePage,
  ]);

  useEffect(() => {
    if (!isCreatePage) return;
    const hydrated = mapIdsToOptions(watchedArtistIds, artists);
    if (!sameOptionIds(selectedArtist, hydrated)) {
      setSelectedArtist(hydrated);
      setArtistsdefaultValue(hydrated);
    }
  }, [
    artists,
    mapIdsToOptions,
    sameOptionIds,
    selectedArtist,
    watchedArtistIds,
    isCreatePage,
  ]);

  useEffect(() => {
    if (!isCreatePage) return;
    const hydrated = mapIdsToOptions(watchedCategoryIds, categoryList);
    if (!sameOptionIds(selectedCategories, hydrated)) {
      setCategories(hydrated);
      setCategorydefaultValue(hydrated);
    }
  }, [
    categoryList,
    mapIdsToOptions,
    sameOptionIds,
    selectedCategories,
    watchedCategoryIds,
    isCreatePage,
  ]);
  const watchedFormValues = useWatch({ control });
  const draftReadyRef = useRef(false);

  const buildDraftPayload = useCallback(
    (formValues) => ({
      formValues: sanitizeDraftValue(formValues),
      eventstartDate: sanitizeDraftValue(eventStartingDate),
      eventEndDate: sanitizeDraftValue(eventEndingDate),
      bookingdate: sanitizeDraftValue(bookingStartingDate),
      bookingEnd: sanitizeDraftValue(bookingEndingDate),
      aboutTextEditorValue: aboutTextEditorValue || "",
      termsTextEditorValue: termsTextEditorValue || "",
      locationValue: locationValue || "",
      latAndLng,
      daynamicImage: daynamicImage || "",
      daynamicLayoutImage: daynamicLayoutImage || "",
      profileImage: profileImage || "",
      islayoutImage: islayoutImage || "",
    }),
    [
      aboutTextEditorValue,
      termsTextEditorValue,
      locationValue,
      latAndLng,
      eventStartingDate,
      eventEndingDate,
      bookingStartingDate,
      bookingEndingDate,
      daynamicImage,
      daynamicLayoutImage,
      profileImage,
      islayoutImage,
    ]
  );

  const persistDraft = useCallback(
    (formValues) => {
      if (!isCreatePage) return;
      const payload = buildDraftPayload(formValues);
      window.sessionStorage.setItem(CREATE_EVENT_DRAFT_KEY, JSON.stringify(payload));
    },
    [buildDraftPayload, isCreatePage]
  );

  const clearCreateEventDraft = useCallback(() => {
    window.sessionStorage.removeItem(CREATE_EVENT_DRAFT_KEY);
  }, []);

  const saveCreateEventDraftNow = useCallback(() => {
    if (!isCreatePage) return;
    persistDraft(getValues());
    dispatch(
      showToast({
        show: false,
        message: "Draft saved",
        type: "success",
      })
    );
  }, [dispatch, getValues, isCreatePage, persistDraft]);
  const { fields, append, prepend, remove, swap, move, insert, replace } =
    useFieldArray({
      control,
      name: "tickets",
    });

  const watchedClub = watch("club");
  const effectiveClubIdForEmployees = useMemo(() => {
    if (isClubManager && managedClubId) return String(managedClubId);
    if (isAdmin && watchedClub?.id) return String(watchedClub.id);
    return null;
  }, [isAdmin, isClubManager, managedClubId, watchedClub]);

  const effectiveClubIdForCatalog = useMemo(() => {
    if (isClubManager && managedClubId) return String(managedClubId);
    if (watchedClub?.id) return String(watchedClub.id);
    const fromItem =
      catalogEventItem?.clubid ??
      catalogEventItem?.clubId ??
      state?.item?.clubid ??
      state?.item?.clubId;
    if (fromItem) return String(fromItem);
    return null;
  }, [
    isClubManager,
    managedClubId,
    watchedClub,
    catalogEventItem,
    state?.item,
  ]);

  const catalogManageHref = isAdmin && effectiveClubIdForCatalog
    ? `/clubs-management/catalog/${effectiveClubIdForCatalog}`
    : isClubManager
      ? "/club-catalog"
      : null;

  useEffect(() => {
    if (isCreatePage || !state?.item?._id) return undefined;
    let cancelled = false;
    eventService
      .getEventDetails(state.item._id)
      .then((res) => {
        const event = unwrapEventRecord(res);
        if (cancelled || !event || typeof event !== "object") return;
        setCatalogEventItem(event);
        const seeded = snapshotIdsFromEvent(event);
        setValue("addonItemIds", seeded.addonItemIds);
        setValue("preorderItemIds", seeded.preorderItemIds);
        setValue("addonPriceOverrides", seeded.addonPriceOverrides);
        setValue("preorderPriceOverrides", seeded.preorderPriceOverrides);
        setValue("addonStockQty", seeded.addonStockQty);
        setValue("preorderStockQty", seeded.preorderStockQty);
        setValue("offlineAssignedUserIds", [
          ...new Set([
            ...pickOfflineAssignedUserIds(event),
            ...pickOnGroundAssignedUserIds(event),
          ]),
        ]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isCreatePage, state?.item?._id, setValue]);

  const [clubEmployeeOptions, setClubEmployeeOptions] = useState([]);
  const [clubEmployeesLoading, setClubEmployeesLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin && !isClubManager) return;
    if (!effectiveClubIdForEmployees) {
      setClubEmployeeOptions([]);
      return;
    }
    let cancelled = false;
    setClubEmployeesLoading(true);
    const clubId = effectiveClubIdForEmployees;
    Promise.all([
      fetchClubEmployees(clubId).catch(() => null),
      fetchClubOperators(clubId).catch(() => null),
    ])
      .then(([employeeRes, operatorRes]) => {
        const staffRows = [
          ...asClubEmployeesList(employeeRes?.data ?? employeeRes).map(
            (row) => ({ row, source: "employee" })
          ),
          ...asClubOperatorsList(operatorRes?.data ?? operatorRes).map(
            (row) => ({ row, source: "operator" })
          ),
        ];
        const seen = new Set();
        const opts = [];
        staffRows.forEach(({ row, source }) => {
          if (!isOnGroundAssignableStaff(row, source)) return;
          const id =
            source === "operator"
              ? pickClubStaffUserId(row)
              : pickClubEmployeeUserId(row);
          if (!id || seen.has(id)) return;
          seen.add(id);
          const role = onGroundRoleForRow(row, source);
          const roleLabel =
            role === "CLUB_OPERATOR"
              ? t("ON_GROUND_ROLE_OPERATOR")
              : t("ON_GROUND_ROLE_EMPLOYEE");
          opts.push({
            label: `${formatClubEmployeeOptionLabel(row)} · ${roleLabel}`,
            value: id,
            id,
            role,
          });
        });
        if (!cancelled) setClubEmployeeOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setClubEmployeeOptions([]);
      })
      .finally(() => {
        if (!cancelled) setClubEmployeesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveClubIdForEmployees, isAdmin, isClubManager, t]);

  useEffect(() => {
    if ((!isAdmin && !isClubManager) || clubEmployeesLoading) return;
    if (!effectiveClubIdForEmployees) return;
    const current = getValues("offlineAssignedUserIds");
    const list = Array.isArray(current) ? current : [];
    const allowed = new Set(
      (clubEmployeeOptions || []).map((option) => String(option.id))
    );
    const next = list.filter((id) => allowed.has(String(id)));
    if (next.length !== list.length) {
      setValue("offlineAssignedUserIds", next);
    }
  }, [
    clubEmployeesLoading,
    effectiveClubIdForEmployees,
    clubEmployeeOptions,
    isAdmin,
    isClubManager,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    if (!isAdmin) return;
    if (state?.page === "add") {
      setValue("club", platformOpt);
      return;
    }
    const id = state?.item?.clubid || state?.item?.clubId;
    if (!id) {
      setValue("club", platformOpt);
      return;
    }
    const c = (clubList || []).find((x) => x._id === id);
    if (c) {
      setValue("club", {
        label: `${c.name} (${c.slug})${
          c.isactive === false ? ` [${t("CLUB_STATUS_INACTIVE")}]` : ""
        }`,
        value: c._id,
        id: c._id,
      });
    } else {
      setValue("club", {
        label: `${id}`,
        value: id,
        id,
      });
    }
  }, [isAdmin, state?.page, state?.item, clubList, setValue, platformOpt, t]);

  useEffect(() => {
    if (!isCreatePage) {
      draftReadyRef.current = false;
      return;
    }
    const rawDraft = window.sessionStorage.getItem(CREATE_EVENT_DRAFT_KEY);
    if (!rawDraft) {
      draftReadyRef.current = true;
      return;
    }
    try {
      const parsedDraft = JSON.parse(rawDraft);
      const draftValues = parsedDraft?.formValues || {};
      const restoredEventStartDate = parseDraftDate(
        parsedDraft?.eventstartDate ?? draftValues?.eventstartDate
      );
      const restoredEventEndDate = parseDraftDate(
        parsedDraft?.eventEndDate ?? draftValues?.eventEndDate
      );
      const restoredBookingStartDate = parseDraftDate(
        parsedDraft?.bookingdate ?? draftValues?.bookingdate
      );
      const restoredBookingEndDate = parseDraftDate(
        parsedDraft?.bookingEnd ?? draftValues?.bookingEnd
      );
      reset({
        ...getValues(),
        ...draftValues,
      });
      if (restoredEventStartDate) {
        setEventStartingDate(restoredEventStartDate);
        setValue("eventstartDate", restoredEventStartDate);
      }
      if (restoredEventEndDate) {
        setEventEndingDate(restoredEventEndDate);
        setValue("eventEndDate", restoredEventEndDate);
      }
      if (restoredBookingStartDate) {
        setBookingStartingDate(restoredBookingStartDate);
        setValue("bookingdate", restoredBookingStartDate);
      }
      if (restoredBookingEndDate) {
        setBookingEndingDate(restoredBookingEndDate);
        setValue("bookingEnd", restoredBookingEndDate);
      }

      if (typeof parsedDraft?.aboutTextEditorValue === "string") {
        setAboutTextEditorValue(parsedDraft.aboutTextEditorValue);
        setAboutTextEditorValueCount(
          htmlToPlainText(parsedDraft.aboutTextEditorValue).length
        );
      }
      if (typeof parsedDraft?.termsTextEditorValue === "string") {
        setTermsTextEditorValue(parsedDraft.termsTextEditorValue);
        setTermsTextEditorValueCount(
          htmlToPlainText(parsedDraft.termsTextEditorValue).length
        );
      }
      if (typeof parsedDraft?.locationValue === "string") {
        setLocationValue(parsedDraft.locationValue);
      }
      if (Array.isArray(draftValues?.language)) {
        setLanguagedefaultValue(draftValues.language);
        setSelectedLanguages(draftValues.language);
      }
      if (Array.isArray(draftValues?.genreid)) {
        setGenresdefaultValue(draftValues.genreid);
        setSelectedGenre(draftValues.genreid);
      }
      if (Array.isArray(draftValues?.category)) {
        setCategorydefaultValue(draftValues.category);
        setCategories(draftValues.category);
      }
      if (Array.isArray(draftValues?.artist)) {
        setArtistsdefaultValue(draftValues.artist);
        setSelectedArtist(draftValues.artist);
      }
      if (draftValues?.organiser && typeof draftValues.organiser === "object") {
        setOrganiserdefaultValue(draftValues.organiser);
      }
      if (parsedDraft?.latAndLng) {
        setLatAndLng(parsedDraft.latAndLng);
      }
      if (parsedDraft?.daynamicImage) {
        setDaynamicImage(parsedDraft.daynamicImage);
      }
      if (parsedDraft?.daynamicLayoutImage) {
        setDaynamicLayoutImage(parsedDraft.daynamicLayoutImage);
      }
      if (typeof parsedDraft?.profileImage === "string") {
        setProfileImage(parsedDraft.profileImage || null);
      }
      if (typeof parsedDraft?.islayoutImage === "string") {
        setIsLayoutImage(parsedDraft.islayoutImage || null);
      }

      dispatch(
        showToast({
          show: false,
          message: "Draft restored",
          type: "success",
        })
      );
    } catch {
      clearCreateEventDraft();
    } finally {
      draftReadyRef.current = true;
    }
  }, [
    clearCreateEventDraft,
    dispatch,
    getValues,
    isCreatePage,
    reset,
    setAboutTextEditorValue,
    setAboutTextEditorValueCount,
    setTermsTextEditorValue,
    setTermsTextEditorValueCount,
    setValue,
  ]);

  useEffect(() => {
    if (!isCreatePage || !draftReadyRef.current) return;
    const timeoutId = setTimeout(() => {
      persistDraft(watchedFormValues);
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [
    watchedFormValues,
    isCreatePage,
    persistDraft,
    aboutTextEditorValue,
    termsTextEditorValue,
    locationValue,
    latAndLng,
    daynamicImage,
    daynamicLayoutImage,
    profileImage,
    islayoutImage,
  ]);

  return (
    <>
      <CreateEditEventComponent1

        selectedCategories={selectedCategories}
        setCategories={setCategories}
        selectedArtist={selectedArtist}
        setSelectedArtist={setSelectedArtist}
        selectedLanguages={selectedLanguages}
        setSelectedLanguages={setSelectedLanguages}
        selectedGenre={selectedGenre}
        setSelectedGenre={setSelectedGenre}
        formState={formState}
        register={register}
        control={control}
        handleSubmit={handleSubmit}
        reset={reset}
        watch={watch}
        setValue={setValue}
        errors={formState.errors}
        trigger={trigger}
        setFocus={setFocus}
        fields={fields}
        append={append}
        prepend={prepend}
        remove={remove}
        swap={swap}
        move={move}
        insert={insert}
        replace={replace}
        pageTitle={pageTitle}
        eventForm={eventForm}
        onChangeAutocomplate={onChangeAutocomplate}
        selectInputRef={selectInputRef}
        artistSelectInputRef={artistSelectInputRef}
        languageSelectInputRef={languageSelectInputRef}
        organiserSelectInputRef={organiserSelectInputRef}
        clubSelectInputRef={clubSelectInputRef}
        showClubField={isAdmin}
        showTableBookingField={isAdmin || isClubManager}
        effectiveClubIdForTableBooking={effectiveClubIdForEmployees}
        effectiveClubIdForCatalog={effectiveClubIdForCatalog}
        catalogEventItem={catalogEventItem}
        catalogManageHref={catalogManageHref}
        canManageCatalog={isAdmin || isClubManager}
        allowOrganiserPick={isAdmin || isClubManager}
        showOfflineEmployeeField={isAdmin || isClubManager}
        clubEmployeeOptions={clubEmployeeOptions}
        clubEmployeesLoading={clubEmployeesLoading}
        clubOptionsForEventForm={clubOptionsForEventForm}
        isCreatePage={isCreatePage}
        saveCreateEventDraftNow={saveCreateEventDraftNow}
        categorySelectInputRef={categorySelectInputRef}
        genresSelectInputRef={genresSelectInputRef}
        formatOptionLabel={formatOptionLabel}
        components={{ Option: InputOption }}
        textTitlecount={textTitlecount}
        handleTextTitleChange={handleTextTitleChange}
        genresList={genresList}
        onChangeArtistType={(options) => {
          setArtistsdefaultValue(options);
          if (Array.isArray(options)) {
            setArtistsState(options.map((opt) => opt.value));
          }
        }}
        onChangeLanguage={(options) => {
          setLanguagedefaultValue(options);
          if (Array.isArray(options)) {
            setLanguage(options.map((opt) => opt.value));
          }
        }}
        onChangeGenre={(options) => {
          setGenresdefaultValue(options);
          if (Array.isArray(options)) {
            setGenres(options.map((opt) => opt.value));
          }
        }}
        onChangeOrganiserType={(options) => {
          setOrganiserdefaultValue(options);
          if (Array.isArray(options)) {
            setOrganiserState(options.map((opt) => opt.value));
          }
        }}
        onChangeCategory={(options) => {
          setCategorydefaultValue(options);
          if (Array.isArray(options)) {
            setCategoryState(options.map((opt) => opt.value));
          }
        }}
        languages={languages}
        organiser={organiser}
        artists={artists}
        categories={categoryList}
        uploadThumNailValid={uploadThumNailValid}
        handleUploadThumnail={handleUploadThumnail}
        handleUploadThumnailFile={handleUploadThumnailFile}
        uploadedDocumentThumnailFile={uploadedDocumentThumnailFile}
        language={language}
        profileImage={profileImage}
        createEventFunction={createEventForm}
        handleFormSubmit={handleFormSubmit}
        handleOpenEventPreview={handleOpenEventPreview}
        handleCloseEventPreview={handleCloseEventPreview}
        eventPreview={eventPreview}
        isUploadingDocumentLink={isUploadingDocumentLink}
        dimenstionError={dimenstionError}
        setDimenstionError={setDimenstionError}
        setIsUploadingDocumentLink={setIsUploadingDocumentLink}
        createEventLoaderButton={iscreateEventLoaderButton}
        handleClearUploadedThumnailFile={handleClearUploadedThumnailFile}
        triggerRef={triggerRef}
        breadcrumbHeight={breadcrumbHeight}
        expireformattedDate={expireformattedDate}
        bookingformattedDate={bookingformattedDate}
        placeHolderShimer={placeHolder}
        aboutTextEditorValue={aboutTextEditorValue}
        setAboutTextEditorValue={setAboutTextEditorValue}
        termsTextEditorValue={termsTextEditorValue}
        setTermsTextEditorValue={setTermsTextEditorValue}
        reactQuillRefIns={reactQuillRefIns}
        reactQuillaboutRefIns={reactQuillaboutRefIns}
        setAboutTextEditorValueCount={setAboutTextEditorValueCount}
        setTermsTextEditorValueCount={setTermsTextEditorValueCount}
        aboutTextEditorValueCount={aboutTextEditorValueCount}
        termsTextEditorValueCount={termsTextEditorValueCount}
        locationValue={locationValue}
        state={state}
        onBackArrow={onBackArrow}
        languagedefaultValue={languagedefaultValue}
        genres={genres}
        genresdefaultValue={genresdefaultValue}
        organiserdefaultValue={organiserdefaultValue}
        organiserState={organiserState}
        categoryState={categoryState}
        artistsState={artistsState}
        artistsdefaultValue={artistsdefaultValue}
        categorydefaultValue={categorydefaultValue}
        expireformattedDateEnd={expireformattedDateEnd}
        bookingexpireformattedDateEnd={bookingexpireformattedDateEnd}
        onCheckAction={onCheckAction}
        daynamicImage={daynamicImage}
        soldoutStatus={soldoutStatus}
        formats={formats}
        foundItem={foundItem}
        handleKeyDown={handleKeyDown}
        // CustomLabel={CustomLabel}
        eventStartingDate={eventStartingDate}
        onChangeEventStartingDate={onChangeEventStartingDate}
        bookingStartingDate={bookingStartingDate}
        onChangeBookingStartingDate={onChangeBookingStartingDate}
        eventEndingDate={eventEndingDate}
        onChangeEventEndingDate={onChangeEventEndingDate}
        bookingEndingDate={bookingEndingDate}
        onChangeBookingEndingDate={onChangeBookingEndingDate}
        isEventEndInvalid={isEventEndInvalid}
        isBookingEndInvalid={isBookingEndInvalid}
        isEventStartInvalid={isEventStartInvalid}
        isBookingStartInvalid={isBookingStartInvalid}
        ticketRows={ticketRows}
        eventsForm={eventsForm}
        showStatusModal={showStatusModal}
        CustomLabelEventStart={CustomLabelEventStart}
        CustomLabelEventEnd={CustomLabelEventEnd}
        CustomLabelBookingStart={CustomLabelBookingStart}
        CustomLabelBookingEnd={CustomLabelBookingEnd}
        handleUploadLayoutThumnail={handleUploadLayoutThumnail}
        handleUploadLayoutThumnailFile={handleUploadLayoutThumnailFile}
        setIsUploadingLayoutImageLink={setIsUploadingLayoutImageLink}
        isUploadingLayoutImageLink={isUploadingLayoutImageLink}
        uploadedLayoutImageFile={uploadedLayoutImageFile}
        handleClearUploadedLayoutImageFile={handleClearUploadedLayoutImageFile}
        uploadLayoutThumNailValid={uploadLayoutThumNailValid}
        islayoutImage={islayoutImage}
        daynamicLayoutImage={daynamicLayoutImage}
      />
    </>
  );
};

export default CreateEditEventContainer1;
